import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import {INPUT_BUBBLE_OPERATION} from 'molbio/maps/InputBubbleTemplate';
import InputBubble from 'molbio/maps/linear/InputBubble';
import {Selection} from 'molbio/maps/selectables/Selection';
import ProteinActions from 'molbio/proteins/ProteinActions';
import * as ProteinProperties from 'molbio/proteins/util/ProteinProperties';
import * as sequtil from 'molbio/SeqUtil';
import {FONT_PROXIMA_11, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as keyboard from 'util/Keyboard';

interface Props {
  proteinId: string;
  selection: Selection;
  top: number;
  left: number;
  offset: number;
  isPositionedAbove: boolean;
  onRequestClose: () => void;
  pasteText: string;
  operation: INPUT_BUBBLE_OPERATION;
}

const actionText = {
  [INPUT_BUBBLE_OPERATION.INSERT]: 'insert',
  [INPUT_BUBBLE_OPERATION.REPLACE]: 'replace with',
  [INPUT_BUBBLE_OPERATION.DELETE]: 'delete',
  [INPUT_BUBBLE_OPERATION.CUT]: 'cut',
};

function ProteinInputBubble(props: Props): JSX.Element {
  const [aminoAcids, setAminoAcids] = useState(filterValidAminoAcids(props.pasteText));
  const isDelete = [INPUT_BUBBLE_OPERATION.CUT, INPUT_BUBBLE_OPERATION.DELETE].includes(props.operation);
  const numAAs = isDelete ? props.selection.length() : aminoAcids.length;

  function handleCommitAminoAcids(): void {
    if (props.selection.isNotEmpty()) {
      ProteinActions.replaceAminoAcids(props.proteinId, {
        start: props.selection.start(),
        end: props.selection.end(),
        aminoAcids,
        onSuccess: props.onRequestClose,
      });
    } else {
      ProteinActions.insertAminoAcids(props.proteinId, {
        position: props.selection.start(),
        aminoAcids,
        onSuccess: props.onRequestClose,
      });
    }
  }

  function filterValidAminoAcids(aminoAcids: string): string {
    return aminoAcids
      .split('')
      .filter((aa) => ProteinProperties.areAminoAcidsValid(aa))
      .join('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (keyboard.isEscapeKey(e)) {
      props.onRequestClose();
    }
    if (keyboard.isEnterKey(e)) {
      handleCommitAminoAcids();
    }
  }

  function handleKbdMouseDown(e: React.MouseEvent): void {
    // Prevent the bubble from closing because of the onBlur handler
    e.preventDefault();
  }

  return (
    <div
      style={{
        top: props.top,
        left: props.left,
        width: InputBubble.BUBBLE_WIDTH,
        position: 'absolute',
      }}
    >
      <div
        className={classNames('input-bubble', css(styles.inputBubble))}
        onKeyDown={handleKeyDown}
        onBlur={props.onRequestClose}
        tabIndex={-1}
        style={props.isPositionedAbove ? {bottom: 0} : {}}
        {...getTestProps({component: 'ProteinInputBubble', element: 'root'})}
      >
        {!props.isPositionedAbove && <div className="triangle top" style={{left: props.offset}} />}
        {!isDelete && (
          <input
            className={classNames(css(styles.input), 'mono')}
            type="text"
            value={aminoAcids}
            onChange={(e) => {
              setAminoAcids(filterValidAminoAcids(e.target.value));
            }}
            autoFocus={true}
            spellCheck={false}
          />
        )}
        <p className={css(styles.pressText)}>Press</p>
        <p>
          <a className="enter-btn">
            <kbd onMouseDown={handleKbdMouseDown} onClick={handleCommitAminoAcids}>
              ENTER
            </kbd>
          </a>
          {/* Proteins cannot be circular so the start index is always 1 */}
          {` to ${actionText[props.operation]} ${numAAs} ${benchlingUtil.pluralize(numAAs, 'residue')}
            at position ${sequtil.modelToDisplay(props.selection.start(), 1)}`}
        </p>
        <p>
          <a className="esc-btn">
            <kbd onMouseDown={handleKbdMouseDown} onClick={props.onRequestClose}>
              ESC
            </kbd>
          </a>
          to cancel
        </p>
        {props.isPositionedAbove && <div className="triangle bottom" style={{left: props.offset}} />}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  inputBubble: {
    width: 'inherit',
  },
  input: {
    marginBottom: '10px',
  },
  pressText: {
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '0',
    padding: '0',
  },
});

export default ProteinInputBubble;
